/**
 * TimePicker class for handling pointer events on a clock element.
 */
export default class TimePicker
{
  /**
   * Initializes pointer events for the clock element.
   * @param {HTMLElement} clock - The clock element.
   * @param {any} dotNetHelper - The .NET helper for invoking methods.
   */
  initPointerEvents (clock: HTMLElement, dotNetHelper: any): void
  {
    let isPointerDown: boolean = false;

    /**
     * Handles the pointer down event.
     * @param {PointerEvent} event - The pointer event.
     */
    const startHandler = (event: PointerEvent): void =>
    {
      if (event.button !== 0)
      {
        // Only handle main (left) pointer button.
        return;
      }

      isPointerDown = true;

      // Allow the pointerover event to trigger.
      (event.target as HTMLElement).releasePointerCapture(event.pointerId);

      // Set the selected value to the stick that the pointer went down on.
      if ((event.target as HTMLElement).classList.contains('fe-hour') || (event.target as HTMLElement).classList.contains('fe-minute'))
      {
        const attributeValue: string | null = (event.target as HTMLElement).getAttribute('data-stick-value');
        const stickValue: number = attributeValue ? parseInt(attributeValue) : -1; // Ensure an integer.

        dotNetHelper.invokeMethodAsync('SelectTimeFromStick', stickValue, false);
      }

      event.preventDefault();
    };

    /**
     * Handles the pointer up event.
     * @param {PointerEvent} event - The pointer event.
     */
    const endHandler = (event: PointerEvent): void =>
    {
      if (event.button !== 0)
      {
        // Only handle main (left) pointer button.
        return;
      }

      isPointerDown = false;

      if ((event.target as HTMLElement).classList.contains('fe-hour') || (event.target as HTMLElement).classList.contains('fe-minute'))
      {
        const attributeValue: string | null = (event.target as HTMLElement).getAttribute('data-stick-value');
        const stickValue: number = attributeValue ? parseInt(attributeValue) : -1; // Ensure an integer.

        dotNetHelper.invokeMethodAsync('OnStickClick', stickValue);
      }

      event.preventDefault();
    };

    /**
     * Handles the pointer move event.
     * @param {PointerEvent} event - The pointer event.
     */
    const moveHandler = (event: PointerEvent): void =>
    {
      if (!isPointerDown || (!(event.target as HTMLElement).classList.contains('fe-hour') && !(event.target as HTMLElement).classList.contains('fe-minute')))
      {
        // Only update time from the stick if the pointer is down.
        return;
      }

      const attributeValue: string | null = (event.target as HTMLElement).getAttribute('data-stick-value');
      const stickValue: number = attributeValue ? parseInt(attributeValue) : -1; // Ensure an integer.

      dotNetHelper.invokeMethodAsync('SelectTimeFromStick', stickValue, true);

      event.preventDefault();
    };

    // Add event listeners to the clock element
    clock.addEventListener('pointerdown', startHandler);
    clock.addEventListener('pointerup', endHandler);
    clock.addEventListener('pointercancel', endHandler);
    clock.addEventListener('pointerover', moveHandler);

    // Add a destroy method to clean up event listeners
    (clock as any).destroy = (): void =>
    {
      clock.removeEventListener('pointerdown', startHandler);
      clock.removeEventListener('pointerup', endHandler);
      clock.removeEventListener('pointercancel', endHandler);
      clock.removeEventListener('pointerover', moveHandler);
    };
  }

  /**
   * Destroys pointer events for the clock element.
   * @param {HTMLElement} container - The container element.
   */
  destroyPointerEvents (container: HTMLElement | null): void
  {
    if (container == null)
    {
      return;
    }
    // Clean up event listeners from the picker element
    if (typeof (container as any).destroy === 'function')
    {
      (container as any).destroy();
    }
  }
}
